import * as Yup from 'yup'
import { addDays, isPast, isToday } from 'date-fns'

import { calculateBudgetFromImpressions } from '../../../../Multiplatform/UploadAdCreative/ImpressionsBudgetSection/helpers'
import { getCampaignBidStrategy } from '../../../campaignHelpers'
import { getObjectiveAmountField } from '../../../helpers'
import { formatFrequencyToBE } from '../../../formatters'
import { formatCampaignName } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/Steps/CampaignNameStep/CampaignNameFields/helpers'

import {
  campaignValidation,
  getBudgetValidation,
  getCampaignInitialValues
} from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import {
  PERFORMANCE_GOAL_AMOUNT,
  PERFORMANCE_GOAL_TYPE,
  PERFORMANCE_GOAL_TYPE_AUDIO_COMPLETION_RATE,
  PERFORMANCE_GOAL_TYPE_CPA,
  PERFORMANCE_GOAL_TYPE_CPC,
  PERFORMANCE_GOAL_TYPE_CPIAVC,
  PERFORMANCE_GOAL_TYPE_CPM,
  PERFORMANCE_GOAL_TYPE_CPV,
  PERFORMANCE_GOAL_TYPE_CTR,
  PLANNED_SPEND_AMOUNT
} from '../../../CampaignForms/CampaignDv360Create/CampaignDv360CreateForm/fields'
import { formatDateBE } from '../../../../../constants/dates'
import { OPTION_YES } from '../../../../../constants/forms'
import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { IMPRESSIONS } from '../../../../Multiplatform/UploadAdCreative/fields'
import { getImpressionsValidation } from '../../../../Multiplatform/UploadAdCreative/ImpressionsBudgetSection/validation'
import { FREQUENCY_IMPRESSIONS, FREQUENCY_OPTION, FREQUENCY_TIME_UNIT } from '../../../fields'
import { LIST_ITEM_OPTION, OPTION_NEW_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'

export const PACING_TYPE = 'pacing_type'
export const PACING_TYPE_AHEAD = 'PACING_TYPE_AHEAD'
export const PACING_TYPE_ASAP = 'PACING_TYPE_ASAP'
export const PACING_TYPE_EVEN = 'PACING_TYPE_EVEN'

export const pacingTypeOptions = [
  {
    value: PACING_TYPE_AHEAD,
    label: 'Ahead (Front Loaded)'
  },
  {
    value: PACING_TYPE_ASAP,
    label: 'ASAP'
  },
  {
    value: PACING_TYPE_EVEN,
    label: 'Even'
  }
]

export const objectivesList = [
  {
    value: PERFORMANCE_GOAL_TYPE_CPA,
    label: 'Cost per conversion (CPA)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_CPM,
    label: 'Cost per thousand impressions (CPM)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_CPIAVC,
    label: 'Cost per impression audible and visible at completion (CPIAVC)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_CPC,
    label: 'Cost per click (CPC)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_CTR,
    label: 'Click Through Rate (CTR)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_CPV,
    label: 'Cost per YouTube video view (CPV)'
  },
  {
    value: PERFORMANCE_GOAL_TYPE_AUDIO_COMPLETION_RATE,
    label: 'Audio completion rate goal'
  }
]

export const getInitialValues = ({ isCampaignsExist }) => {
  const campaignsInitialValues = getCampaignInitialValues(isCampaignsExist)

  return {
    ...campaignsInitialValues,
    [PERFORMANCE_GOAL_TYPE]: PERFORMANCE_GOAL_TYPE_CPA,
    // frequency
    [FREQUENCY_OPTION]: OPTION_YES,
    [FREQUENCY_TIME_UNIT]: 'TIME_UNIT_LIFETIME',
    [PACING_TYPE]: PACING_TYPE_AHEAD
  }
}

export const getValidationSchema = ({
  remainingBudget,
  remainingImpressions,
  currencySymbol,
  allocationTypeImpressions
}) => {
  return Yup.object().shape({
    ...campaignValidation,

    [PERFORMANCE_GOAL_AMOUNT]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () => Yup.string().required('This field is required')
    }),

    ...(allocationTypeImpressions
      ? {
          [IMPRESSIONS]: Yup.number().when(LIST_ITEM_OPTION, {
            is: OPTION_NEW_LIST_ITEM,
            then: () => getImpressionsValidation({ remainingImpressions })
          })
        }
      : {
          [PLANNED_SPEND_AMOUNT]: Yup.number().when(LIST_ITEM_OPTION, {
            is: OPTION_NEW_LIST_ITEM,
            then: () => getBudgetValidation({ remainingBudget, currencySymbol })
          })
        }),

    [FREQUENCY_IMPRESSIONS]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () =>
        Yup.string().when(FREQUENCY_OPTION, {
          is: OPTION_YES,
          then: () => Yup.string().required('This field is required')
        })
    })
  })
}

export const transformValuesToBE = ({ values, right, account, selectedPartnership }) => {
  const {
    start_date: rightStartDate,
    end_date: rightEndDate,
    allocation_type: rightAllocationType,
    bid_cap: rightBidCap
  } = right

  // If right start date is in the past or today, then set campaign start date to tomorrow
  const startDate =
    isPast(new Date(rightStartDate)) || isToday(new Date(rightStartDate))
      ? formatDateBE(addDays(new Date(), 1))
      : formatDateBE(new Date(rightStartDate))

  const endDate = formatDateBE(new Date(rightEndDate))

  const objectiveAmountField = getObjectiveAmountField(values[PERFORMANCE_GOAL_TYPE])

  const getCampaignBudget = () => {
    if (rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS) {
      return calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })
    } else {
      return values[PLANNED_SPEND_AMOUNT]
    }
  }

  const campaignName = formatCampaignName(values, selectedPartnership)

  return {
    account,
    status: 'active',
    name: campaignName,

    frequency_cap: formatFrequencyToBE(values),

    budget_lifetime: getCampaignBudget(),

    date_start: startDate,
    date_stop: endDate,

    objective: values[PERFORMANCE_GOAL_TYPE],
    [objectiveAmountField]: values[PERFORMANCE_GOAL_AMOUNT],

    bid_strategy: getCampaignBidStrategy(values[PERFORMANCE_GOAL_TYPE]),

    pacing: {
      pacing_period: 'PACING_PERIOD_FLIGHT',
      pacing_type: values[PACING_TYPE]
    }
  }
}
